import React from "react";
import { useSetAtom } from "jotai";

// region Languages
import useTranslation from "@languages/useTranslation";
import { AnnouncementMessages } from "@languages/interfaces";
// endregion Languages
// region Libraries
import { IconButton } from "@libraries/mui/components";
import { Close, Notifications } from "@libraries/mui/icons";
// endregion Libraries
// region Hooks
import { useExposure } from "@hooks/useExposure";
import { useMyAnnouncements } from "@hooks/useMyAnnouncements";
// endregion Hooks
// region Molecules
import { openNotificationsRequestAtom } from "@molecules/NotificationCenter";
// endregion Molecules
// region Styles
import * as Styled from "./styles";
// endregion Styles

/**
 * Launcher banner for the highlighted announcement. Shows at most ONE banner:
 * the most recent active announcement flagged with Highlight that the user has
 * not dismissed (X) nor clicked — both actions close the banner and neither
 * removes the announcement, which stays in the bell until it expires.
 * Standing on screen for a while already marks it as read, so the reach
 * counters do not depend on the user closing it. Rides the same Apollo cache as
 * the navbar bell, so it does not schedule its own poll.
 */
const AnnouncementBanner: React.FC = () => {

  const { t } = useTranslation();
  const { announcements, markReadOnExposure, registerClick, dismiss } = useMyAnnouncements({ poll: false });

  // Records come ordered by StartDate desc, so the first match is the newest.
  const banner = announcements.find((item) => item.highlight && !item.clickedAt && !item.dismissedAt);

  const exposureRef = useExposure<HTMLDivElement>(
    Boolean(banner) && !banner?.readAt,
    () => banner && markReadOnExposure(banner.id)
  );

  const requestOpenNotifications = useSetAtom(openNotificationsRequestAtom);

  if (!banner) return null;

  const handleCtaClick = () => {
    registerClick(banner.id);

    if (banner.link) window.open(banner.link, "_blank", "noopener,noreferrer");
  };

  const handleDismiss = () => dismiss(banner.id);

  return (
    <Styled.Container ref={exposureRef}>
      {/* Opens the navbar bell's dropdown: the banner shows one announcement, the bell all of them. */}
      <Styled.IconChip
        type="button"
        title={t(AnnouncementMessages.bellTooltip)}
        aria-label={t(AnnouncementMessages.bellTooltip)}
        onClick={() => requestOpenNotifications((request) => request + 1)}
      >
        <Notifications />
      </Styled.IconChip>
      <Styled.Content>
        <Styled.Eyebrow>{t(AnnouncementMessages.bannerHighlight)}</Styled.Eyebrow>
        <Styled.Title>{banner.title}</Styled.Title>
        <Styled.Message>{banner.message}</Styled.Message>
      </Styled.Content>
      {banner.link && (
        <Styled.CtaButton type="button" onClick={handleCtaClick}>
          {banner.linkLabel || t(AnnouncementMessages.defaultCta)}
        </Styled.CtaButton>
      )}
      <IconButton
        aria-label={t(AnnouncementMessages.bannerDismiss)}
        title={t(AnnouncementMessages.bannerDismiss)}
        onClick={handleDismiss}
        size="small"
      >
        <Close fontSize="small" />
      </IconButton>
    </Styled.Container>
  );
};

export default AnnouncementBanner;
